import { Link } from "@tanstack/react-router";
import { Phone, Mail, MapPin } from "lucide-react";
import logoMark from "@/assets/logo-rrlex-mark.webp";
import { useT, useMedia } from "@/lib/i18n";
import { WhatsAppLink } from "./WhatsAppLink";
import siteConfig from "@/config/site.json";
import navigation from "@/config/navigation.json";

export function Footer() {
  const { tk } = useT();
  const logo = useMedia("brand.logo.mark", logoMark, siteConfig.brand.name);
  return (
    <footer className="border-t border-gold/20">
      <div className="max-w-7xl mx-auto px-6 py-16 grid gap-12 md:grid-cols-4">
        <div className="md:col-span-1">
          <Link
            to="/"
            aria-label={logo.alt}
            className="flex items-center justify-center rounded-full h-24 w-24 bg-black"
          >
            <img src={logo.url} alt={logo.alt} width={96} height={96} />
          </Link>
          <p className="mt-5 text-sm text-muted-foreground leading-relaxed">
            {tk(siteConfig.brand.tagline_key, siteConfig.brand.tagline_default)}
          </p>
        </div>

        {navigation.footer.columns.map((col) => (
          <div key={col.titleKey}>
            <h3 className="text-xs tracking-[0.3em] uppercase text-gold mb-5">
              {tk(col.titleKey, col.titleDefault)}
            </h3>
            <ul className="space-y-3 text-sm text-foreground/70">
              {col.items.map((item) => (
                <li key={`${col.titleKey}:${item.to}:${item.labelKey}`}>
                  <Link to={item.to as "/"} className="hover:text-gold">
                    {tk(item.labelKey, item.labelDefault)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div>
          <h3 className="text-xs tracking-[0.3em] uppercase text-gold mb-5">{tk("footer.d8a53e", "Contacto")}</h3>
          <ul className="space-y-3 text-sm text-foreground/70">
            {siteConfig.contact.phones.map((p) => (
              <li key={p.number}>
                <a
                  href={`tel:${p.number.replace(/\s+/g, "")}`}
                  className="flex items-center gap-2 hover:text-gold"
                >
                  <Phone size={14} className="text-gold" />
                  <span>{p.number}</span>
                </a>
              </li>
            ))}
            {siteConfig.contact.emails.map((e) => (
              <li key={e.address}>
                <a
                  href={`mailto:${e.address}`}
                  className="flex items-center gap-2 hover:text-gold"
                >
                  <Mail size={14} className="text-gold" />
                  <span>{e.address}</span>
                </a>
              </li>
            ))}

            <WhatsAppLink variant="footer" />
          </ul>
        </div>

        <div>
          <h3 className="text-xs tracking-[0.3em] uppercase text-gold mb-5">{tk("footer.4e38ee", "Despachos")}</h3>
          <ul className="space-y-3 text-sm text-foreground/70">
            {siteConfig.addresses.slice(0, 1).map((a) => (
              <li key={a.id} className="flex gap-2">
                <MapPin size={14} className="text-gold mt-1 shrink-0" />
                <span>{a.line}</span>
              </li>
            ))}
            <li>
              <Link
                to="/about/oficinas"
                className="inline-flex items-center gap-2 mt-2 rounded-full border border-gold/40 px-4 py-2 text-[10px] tracking-[0.3em] uppercase text-gold hover:bg-gold hover:text-primary-foreground transition-colors"
              >
                {tk("footer.allOffices", "All our offices")}
              </Link>
            </li>
          </ul>

        </div>
      </div>

      <div className="border-t border-gold/15 bg-black/30">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-center gap-3 text-xs tracking-[0.2em] uppercase text-muted-foreground text-center">
          <span>{tk("footer.developedBy", "Desarrollado por")}</span>
          <a
            href="https://pulso33.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-4 py-2 text-gold hover:bg-gold hover:text-primary-foreground transition-colors font-semibold"
          >
            PULSO33
          </a>
        </div>
      </div>

      <div className="border-t border-gold/15">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row justify-between gap-3 text-xs tracking-[0.2em] uppercase text-muted-foreground">
          <span>
            © {new Date().getFullYear()} {siteConfig.brand.name} — {tk("footer.2437c5", "Todos los derechos reservados")}
          </span>
          <span className="flex gap-5 items-center flex-wrap">
            {navigation.footer.legal.map((l) => (
              <Link key={l.labelKey} to={l.to as "/"} className="hover:text-gold">
                {tk(l.labelKey, l.labelDefault)}
              </Link>
            ))}
          </span>
        </div>
      </div>
    </footer>
  );
}
